import { useEffect } from 'react';
import useSeo, { type SeoData } from '@/hooks/useSeo';

export default function ArtigoFrete() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoData: SeoData = {
    title: 'Frete China para Brasil: Guia Completo 2026 – Opções, Prazos e Custos',
    description: 'Guia completo de frete China para Brasil via CSSBuy Spreadsheet. Compare DHL, EMS, China Post e linhas econômicas. Dicas para economizar no frete internacional e prazos atualizados 2026.',
    keywords: 'frete China Brasil, envio China Brasil, CSSBuy frete, DHL China Brasil, prazo importação China',
    canonicalPath: '/artigo/frete-china-brasil',
    ogType: 'article',
    ogImage: 'https://readdy.ai/api/search-image?query=Minimalist%20world%20globe%20and%20cardboard%20shipping%20boxes%20on%20a%20clean%20surface%20with%20soft%20natural%20light%2C%20warm%20cream%20and%20beige%20tones%2C%20editorial%20product%20photography%2C%20international%20shipping%20and%20logistics%20concept&width=1200&height=630&seq=artigo-frete-hero-seo&orientation=landscape',
    schemaJsonLd: {
      '@type': 'BlogPosting',
      headline: 'Frete China para Brasil: Guia Completo 2026',
      description: 'Guia completo de frete da China para o Brasil usando CSSBuy Spreadsheet com tabela de métodos, prazos, custos e dicas de economia.',
      datePublished: '2026-06-05',
      dateModified: '2026-06-17',
      author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
    },
  };
  useSeo(seoData);

  return (
    <main className="min-h-screen bg-background-50">
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <nav className="mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Home
          </a>
        </nav>

        <header className="mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide mb-6">
            FRETE
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Frete China para Brasil: Guia Completo 2026
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
            Entenda todas as opções de frete disponíveis no CSSBuy Spreadsheet, compare
            prazos e custos, e aprenda a economizar nas suas importações.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
            <span>Atualizado: Junho 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>Leitura: 9 min</span>
          </div>
        </header>

        <div className="w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-14">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20world%20globe%20and%20cardboard%20shipping%20boxes%20on%20a%20clean%20surface%20with%20soft%20natural%20light%2C%20warm%20cream%20and%20beige%20tones%2C%20editorial%20product%20photography%2C%20international%20shipping%20and%20logistics%20concept&width=1200&height=630&seq=artigo-frete-hero-v2&orientation=landscape"
            alt="Frete China Brasil"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Opções de Frete Disponíveis
          </h2>
          <p className="text-foreground-700 leading-relaxed mb-5">
            O <strong>CSSBuy Spreadsheet</strong> oferece diversas opções de envio
            da China para o Brasil. Cada método tem suas vantagens em termos de
            prazo, custo e segurança.
          </p>

          <div className="overflow-x-auto rounded-xl border border-background-200/70">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary-500 text-background-50">
                  <th className="text-left px-5 py-3 font-semibold">Método</th>
                  <th className="text-left px-5 py-3 font-semibold">Prazo</th>
                  <th className="text-left px-5 py-3 font-semibold">Custo</th>
                  <th className="text-left px-5 py-3 font-semibold">Rastreio</th>
                  <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">
                    Recomendação
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['DHL Express', '7-12 dias', 'Alto', 'Sim', 'Urgentes'],
                  ['EMS', '15-25 dias', 'Médio', 'Sim', 'Equilíbrio'],
                  ['China Post', '25-45 dias', 'Baixo', 'Parcial', 'Econômico'],
                  ['Linha Econômica', '30-60 dias', 'Muito Baixo', 'Limitado', 'Baixo custo'],
                  ['Sea Freight', '45-90 dias', 'Baixo (grande volume)', 'Sim', 'Carga grande'],
                ].map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      i % 2 === 0 ? 'bg-background-50' : 'bg-background-100/60'
                    } border-b border-background-200/50 last:border-0`}
                  >
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className={`px-5 py-3 ${
                          j === 4 ? 'hidden md:table-cell' : ''
                        } ${j === 0 ? 'font-semibold text-foreground-900' : 'text-foreground-700'}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Dicas para Economizar no Frete
          </h2>
          <div className="space-y-4">
            {[
              {
                title: 'Consolide seus pedidos',
                desc: 'Agrupe vários produtos em um único envio para diluir o custo do frete por item.',
              },
              {
                title: 'Escolha o método certo',
                desc: 'Para itens não urgentes, linhas econômicas podem reduzir o custo em até 60%.',
              },
              {
                title: 'Atenção ao peso',
                desc: 'O frete é calculado por peso. Evite produtos desnecessariamente pesados.',
              },
              {
                title: 'Aproveite promoções',
                desc: 'Fique atento a cupons e descontos sazonais oferecidos na plataforma.',
              },
            ].map((tip, i) => (
              <div
                key={i}
                className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60"
              >
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-lightbulb-line text-secondary-600"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">
                    {tip.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {tip.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
            Calcule seu Frete
          </h2>
          <p className="text-background-50/85 mb-6 max-w-md mx-auto leading-relaxed">
            Use o CSSBuy Spreadsheet para simular custos de frete e planejar suas
            importações com precisão.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
          >
            CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </section>

        <section className="mt-12 pt-10 border-t border-background-200/70">
          <h3 className="text-lg font-semibold text-foreground-900 mb-4">
            Leia também
          </h3>
          <div className="flex flex-wrap gap-3">
            <a
              href="/artigo/cssbuy-spreadsheet-guia-completo"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Guia Completo CSSBuy
            </a>
            <a
              href="/artigo/seguranca-cssbuy-spreadsheet"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Segurança CSSBuy
            </a>
            <a
              href="/"
              className="px-4 py-2 bg-primary-100 hover:bg-primary-200 rounded-full text-sm text-primary-700 transition-colors font-medium"
            >
              ← Voltar para Home
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}